import type { AxiosError, AxiosInstance } from "axios";
import axios from "axios";
import i18n from "@/plugins/vueI18n";
import type { ApiResponse } from "@/models/api";

// API client options
export type ApiClientOptions = {
  webScoped?: boolean;
  clientScoped?: boolean;
}
const DEFAULT_OPTIONS = {
  webScoped: true,
  clientScoped: true,
};

const hostNameParts = window.location.hostname.split('.');

const domain = hostNameParts.slice(hostNameParts.length - 2).join('.');
if (import.meta.env.VITE_API_ALLOWED_DOMAINS.split(',').indexOf(domain) === -1) {
  throw new Error('Domain not allowed');
}

export const clientScope = hostNameParts[ 0 ];

const baseUrl = import.meta.env.VITE_API_BASE_URL.replace('%domain%', domain);
const webScope = `${import.meta.env.VITE_API_NAMESPACE}/${import.meta.env.VITE_API_VERSION}`;

export class ApiError extends Error {
  public readonly code: number | null;
  public readonly message: string;
  public readonly messageCode: string | null;

  constructor(code: number | null, message: string, messageCode: string | null) {
    super(message);
    this.name = `ApiError`;
    this.code = code;
    this.message = message;
    this.messageCode = messageCode;
  }
}

function createAxiosInstance(baseUrl: string) {
  const instance = axios.create({
    baseURL: baseUrl,
    withCredentials: true,
    withXSRFToken: true,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
  });
  instance.interceptors.request.use(function (config) {
    return config;
  }, function (error) {
    return Promise.reject(error);
  });
  instance.interceptors.response.use(function (response) {
    return response;
  }, function (error: AxiosError) {
    console.log(error);
    let statusCode = null;
    let messageCode = null;
    let message = i18n.global.t('error.unknown');
    if (error.response) {
      const data = error.response.data as ApiResponse<void>;
      switch (error.response.status) {
        case 400:
          message = i18n.global.t(`error.badRequest`);
          break;
        case 401:
          message = i18n.global.t(`error.unauthorized`);
          break;
        case 422:
          if (data.errorDetails) {
            message = data.errorDetails.message ?? i18n.global.t(`error.unprocessableEntity`);
          } else {
            message = i18n.global.t(`error.unprocessableEntity`);
          }
          break;
        case 500:
          message = i18n.global.t(`error.internalServerError`);
          break;
        case 520:
        case 521:
          // Internal managed errors, return the response so that we can use the error code to print the correct msg
          messageCode = data.error;
          message = i18n.global.t(data.error ?? 'error.unknown');
          break;
        default:
          message = i18n.global.t(`error.notImplemented`);
      }
    } else if (error.request) {
      message = i18n.global.t('error.network');
    }
    return Promise.reject(new ApiError(statusCode, message, messageCode));
  });
  return instance;
}

// To keep it static only and non-instantiable
export abstract class ApiClient {
  private static readonly http: AxiosInstance = createAxiosInstance(baseUrl);

  public static getClient(options: ApiClientOptions = {}): AxiosInstance {
    const clientOptions = Object.assign({}, DEFAULT_OPTIONS, options);
    const isDisableCache = !!localStorage.getItem('changeit_cf_disabled');

    if (isDisableCache) {
      const currentTime = Date.now();
      ApiClient.http.defaults.params = {
        "nonce": currentTime,
      };
    }


    ApiClient.http.defaults.baseURL = `${baseUrl}${clientOptions.webScoped ? `/${webScope}` : ''}${clientOptions.clientScoped ? `/${clientScope}` : ''}`;
    return ApiClient.http;
  }
}
